/**
 * Il run agentico, passo per passo (F-144, SPEC-025 §9).
 *
 * È la pagina che rende **rileggibile** uno studio: cosa gli è stato detto
 * (istruzioni e domanda, nel primo passo), cosa ha pensato a ogni giro (il
 * testo del modello), quali strumenti ha chiamato e con quali argomenti, cosa
 * gli hanno risposto, cosa gli ha bloccato il cancello, come ha concluso e
 * quali proposte ha consegnato al motore. Senza questa pagina il badge
 * «agentico» sarebbe una promessa: la riconciliazione arriva fin qui e si
 * fermerebbe davanti a un id.
 *
 * Due scelte:
 *  - i passi si mostrano **tutti**, in ordine, con input e output ripiegati
 *    (`<details>`): il lettore normale scorre la colonna dei titoli, chi
 *    verifica apre il passo che gli interessa. Un filtro «solo errori»
 *    nasconderebbe proprio il contesto che spiega l'errore;
 *  - il JSON si mostra **com'è**, senza ricostruirlo in tabelle: è la vista
 *    tecnica, e ogni tool ha una forma diversa. Riformattare vorrebbe dire
 *    scegliere cosa omettere, e la pagina esiste per non omettere.
 */

import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime, formatNumber } from '@/lib/format';
import { Alert, Badge, Card, Spinner } from '@/ui';
import { AgenticBadge } from './AgenticBadge';
import { useAgentRun, type AgentRun, type AgentRunStep } from './api';
import './agent-runs.css';

const EMPTY = '—';

const STATUS_TONE: Record<string, 'positive' | 'negative' | 'neutral' | 'accent' | 'warning'> = {
  completed: 'positive',
  failed: 'negative',
  running: 'warning',
};

const STATUS_LABEL: Record<string, string> = {
  completed: 'concluso',
  failed: 'fallito',
  running: 'in corso',
};

const PURPOSE_LABEL: Record<string, string> = {
  market_close_review: 'Revisione di fine giornata',
  market_open_briefing: 'Resoconto pre-apertura',
  news_review: 'Lettura delle notizie',
  study: 'Studio',
  manual: 'Richiesta manuale',
};

const CREATED_BY_LABEL: Record<string, string> = {
  market_close_review: 'Revisione di fine giornata',
  market_open_briefing: 'Resoconto pre-apertura con analisi differita',
  news_review: 'Lettura quotidiana delle notizie',
  wishlist_revival: 'Ripescaggio dei candidati scaduti',
  manual_request: 'Richiesta manuale',
  test: 'Test',
};

const STEP_LABEL: Record<string, string> = {
  model_call: 'Chiamata al modello',
  tool_call: 'Strumento',
  gate_block: 'Bloccato dal cancello',
  conclusion: 'Conclusione',
  note: 'Nota',
};

const STEP_ICON: Record<string, string> = {
  model_call: '🧠',
  tool_call: '🔧',
  gate_block: '🚧',
  conclusion: '🏁',
  note: '📝',
};

const TERMINATED_LABEL: Record<string, string> = {
  model_stopped: 'il modello ha concluso da sé',
  max_steps: 'raggiunto il massimo di giri',
  max_tool_calls: 'raggiunto il massimo di chiamate agli strumenti',
  deadline: 'scaduto il tempo',
  doom_loop: 'stessa chiamata ripetuta',
  context_exhausted: 'contesto esaurito',
  budget_exhausted: 'budget esaurito',
  repeated_mandate_violations: 'troppe violazioni del mandato',
  suspended: 'sospeso: riprende dopo le analisi chieste',
};

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="trace__field">
      <dt>{label}</dt>
      <dd>{value ?? EMPTY}</dd>
    </div>
  );
}

function pretty(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * L'origine del run, apribile quando la conosciamo: la revisione porta
 * l'id del briefing di mercato, che ha una pagina sua.
 */
function originHref(run: AgentRun): string | null {
  if (run.createdBy.kind !== 'market_close_review' || !run.createdBy.sourceId) return null;
  const mic = /\(([A-Z0-9]{4})\)/.exec(run.createdBy.detail ?? '')?.[1];
  return mic ? `/markets/${mic}/briefings/${run.createdBy.sourceId}` : null;
}

function stepTitle(step: AgentRunStep): string {
  if (step.kind === 'tool_call') return step.toolName ?? 'strumento';
  return STEP_LABEL[step.kind] ?? step.kind;
}

/**
 * Il riassunto di un passo, in una riga: cio' che si legge scorrendo senza
 * aprire niente. Per una chiamata al modello e' il suo testo (il ragionamento
 * visibile), per uno strumento gli argomenti, per un blocco il motivo.
 */
function stepSummary(step: AgentRunStep): string | null {
  const output = asRecord(step.output);
  const input = asRecord(step.input);
  switch (step.kind) {
    case 'model_call': {
      const assistant = asText(output?.assistant);
      const calls = typeof output?.toolCalls === 'number' ? output.toolCalls : null;
      const head = assistant ?? (calls ? `Chiede ${calls} ${calls === 1 ? 'strumento' : 'strumenti'}` : null);
      return head;
    }
    case 'tool_call':
      return step.ok === false && step.errorCode ? `${step.errorCode}${asText(output?.message) ? ` — ${asText(output?.message)}` : ''}` : pretty(step.input).slice(0, 400);
    case 'gate_block':
      return asText(output?.message) ?? asText(output?.reason) ?? asText(input?.reason) ?? step.errorCode;
    case 'conclusion':
      return asText(output?.summary) ?? (step.ok === false ? (step.errorCode ?? 'conclusione non valida') : null);
    default:
      return asText(output?.message) ?? null;
  }
}

function StepRow({ step }: { step: AgentRunStep }) {
  const failed = step.ok === false;
  const input = asRecord(step.input);
  const summary = stepSummary(step);
  const isFirstModelCall = step.kind === 'model_call' && input !== null && ('system' in input || 'user' in input);

  return (
    <li className={`agent-run__step ${failed ? 'agent-run__step--failed' : ''}`}>
      <span className="agent-run__step-icon" aria-hidden="true">
        {STEP_ICON[step.kind] ?? '•'}
      </span>
      <div className="agent-run__step-body">
        <div className="agent-run__step-head">
          <span className="agent-run__step-no">#{step.stepNo}</span>
          <strong>{stepTitle(step)}</strong>
          {step.kind === 'tool_call' && (
            <Badge tone={failed ? 'negative' : 'neutral'}>{failed ? (step.errorCode ?? 'errore') : 'ok'}</Badge>
          )}
          {step.kind === 'gate_block' && <Badge tone="warning">{step.errorCode ?? 'cancello'}</Badge>}
          {step.kind === 'conclusion' && failed && <Badge tone="negative">{step.errorCode ?? 'non valida'}</Badge>}
          <span className="agent-run__step-meta">
            {step.durationMs !== null && <>{formatNumber(step.durationMs)} ms</>}
            {step.promptTokens !== null && <> · {formatNumber(step.promptTokens)} + {formatNumber(step.completionTokens ?? 0)} token</>}
            {' · '}
            {formatDateTime(step.createdAt)}
          </span>
        </div>
        {summary && <p className="agent-run__step-summary">{summary}</p>}
        {isFirstModelCall && (
          <>
            <details className="agent-run__details">
              <summary>Istruzioni date al modello</summary>
              <pre className="agent-run__pre">{pretty(input?.system)}</pre>
            </details>
            <details className="agent-run__details">
              <summary>Domanda e contesto di partenza</summary>
              <pre className="agent-run__pre">{pretty(input?.user)}</pre>
            </details>
          </>
        )}
        {step.kind !== 'model_call' && step.input !== null && step.input !== undefined && (
          <details className="agent-run__details">
            <summary>Argomenti</summary>
            <pre className="agent-run__pre">{pretty(step.input)}</pre>
          </details>
        )}
        {step.output !== null && step.output !== undefined && (
          <details className="agent-run__details">
            <summary>{step.kind === 'model_call' ? 'Risposta del modello' : step.kind === 'conclusion' ? 'Conclusione completa' : 'Risultato'}</summary>
            <pre className="agent-run__pre">{pretty(step.output)}</pre>
          </details>
        )}
      </div>
    </li>
  );
}

export function AgentRunPage() {
  const { runId } = useParams<{ runId: string }>();
  const query = useAgentRun(runId ?? null);

  if (query.isPending) return <Spinner label="Caricamento del run agentico…" />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Alert variant="error" title={notFound ? 'Run non trovato' : 'Errore'}>
        {notFound
          ? 'Questo run non esiste, oppure non è tuo.'
          : query.error instanceof Error
            ? query.error.message
            : 'Errore imprevisto'}
      </Alert>
    );
  }

  const run = query.data;
  const origin = originHref(run);
  const conclusion = asRecord(run.conclusion);
  const conclusionTitles = Array.isArray(conclusion?.titles) ? (conclusion.titles as unknown[]) : [];
  const steps = run.steps ?? [];
  const proposals = run.proposals ?? [];

  return (
    <div className="trace agent-run">
      <header className="page-header">
        <div>
          <h1 className="trace__title">
            {PURPOSE_LABEL[run.purpose] ?? run.purpose} <AgenticBadge />
          </h1>
          <p className="page-header__meta">
            Avviato il {formatDateTime(run.startedAt)}
            {run.finishedAt && <> · concluso il {formatDateTime(run.finishedAt)}</>}
            {' · da '}
            {origin ? (
              <Link to={origin}>{run.createdBy.detail ?? CREATED_BY_LABEL[run.createdBy.kind] ?? run.createdBy.kind}</Link>
            ) : (
              (run.createdBy.detail ?? CREATED_BY_LABEL[run.createdBy.kind] ?? run.createdBy.kind)
            )}
          </p>
        </div>
        <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>{STATUS_LABEL[run.status] ?? run.status}</Badge>
      </header>

      {run.errorMessage && (
        <Alert variant="error" title="Il run è fallito">
          {run.errorMessage}
        </Alert>
      )}

      <Card title="La domanda" subtitle="Il compito assegnato allo studio, prima di qualunque strumento.">
        <p className="agent-run__question">{run.question}</p>
      </Card>

      <Card title="Numeri" subtitle="Quanto ha lavorato e quanto è costato. Il costo è stimato a prezzi unitari fissi.">
        <dl className="trace__fields">
          <Field label="Giri" value={String(run.counts.steps)} />
          <Field label="Chiamate agli strumenti" value={String(run.counts.toolCalls)} />
          <Field label="Blocchi del cancello" value={String(run.counts.gateBlocks)} />
          <Field label="Proposte consegnate" value={String(run.counts.proposals)} />
          <Field label="Importo proposto (valuta base)" value={run.proposalsAmountBase} />
          <Field label="Token" value={`${formatNumber(run.tokens.prompt ?? 0)} + ${formatNumber(run.tokens.completion ?? 0)}`} />
          <Field label="Costo stimato (USD)" value={run.costUsd} />
          <Field label="Modello" value={run.llmModel ? `${run.llmModel} (${run.llmTier})` : run.llmTier} />
          <Field label="Come si è fermato" value={run.terminatedReason ? (TERMINATED_LABEL[run.terminatedReason] ?? run.terminatedReason) : null} />
          <Field label="Impronta del mandato" value={run.mandateHash.slice(0, 16)} />
        </dl>
      </Card>

      {(conclusion || run.conclusionRaw) && (
        <Card title="Conclusione" subtitle="Ciò che lo studio ha risposto alla fine, senza strumenti.">
          {asText(conclusion?.summary) && <p className="agent-run__summary">{asText(conclusion?.summary)}</p>}
          {conclusionTitles.length > 0 && (
            <ul className="agent-run__titles">
              {conclusionTitles.map((entry, index) => {
                const title = asRecord(entry);
                if (!title) return null;
                const action = asText(title.action) ?? 'hold';
                return (
                  <li key={`${asText(title.symbol) ?? index}`}>
                    <Badge tone={action === 'buy' ? 'negative' : action === 'sell' ? 'accent' : 'neutral'}>{action}</Badge>{' '}
                    <strong>{asText(title.symbol) ?? asText(title.instrumentName) ?? '?'}</strong>
                    {asText(title.rating) && <> · {asText(title.rating)}</>}
                    {asText(title.reason) && <span className="agent-run__reason"> — {asText(title.reason)}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {!conclusion && run.conclusionRaw && (
            <details className="agent-run__details" open>
              <summary>Risposta grezza (non conforme allo schema)</summary>
              <pre className="agent-run__pre">{run.conclusionRaw}</pre>
            </details>
          )}
        </Card>
      )}

      <Card
        title="Proposte consegnate al motore"
        subtitle="Ognuna passa dal cancello, dai limiti e dalla verifica: lo studio propone, non esegue."
      >
        {proposals.length === 0 ? (
          <p className="text-muted">Nessuna proposta: lo studio ha concluso senza chiedere di agire.</p>
        ) : (
          <ul className="agent-run__proposals">
            {proposals.map((proposal) => (
              <li key={proposal.id}>
                <Badge tone={proposal.side === 'buy' ? 'negative' : 'accent'}>{proposal.side}</Badge>{' '}
                <Link to={`/auto-trading/proposals/${proposal.id}`}>
                  {proposal.proposedAmount ?? EMPTY} · {proposal.status} →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        title="Passi"
        subtitle="Nell'ordine in cui sono avvenuti: istruzioni, ragionamento, strumenti chiamati e risposte, blocchi del cancello, conclusione."
        flush
      >
        {steps.length === 0 ? (
          <p className="text-muted agent-run__empty">Nessun passo registrato{run.status === 'running' ? ': il run sta ancora partendo.' : '.'}</p>
        ) : (
          <ol className="agent-run__steps">
            {steps.map((step) => (
              <StepRow key={step.stepNo} step={step} />
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
